<template>
  <!-- Header: logo, title, company, status -->
  <div class="flex items-start gap-4 w-full">
    <img
      v-if="selectedRole?.employer_details?.logo"
      :src="selectedRole?.employer_details?.logo"
      class="h-12 w-12 shrink-0 rounded-xl bg-white object-contain ring-1 ring-black/5"
      :alt="
        selectedRole?.employer_details?.name
          ? selectedRole?.employer_details?.name + ' logo'
          : 'Employer logo'
      "
      loading="lazy"
    />
    <span
      v-else
      class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 ring-1 ring-black/5"
    >
      <Building class="h-6 w-6 text-gray-400" />
    </span>

    <div class="min-w-0 flex-1">
      <h2 class="text-lg font-semibold leading-snug text-gray-900">
        {{ selectedRole?.role_name }}
      </h2>
      <p class="mt-0.5 text-sm text-gray-500">{{ selectedRole?.employer_details?.name }}</p>
    </div>

    <JobStatusDropdown
      v-if="selectedRole?.id"
      v-model="roleStatus"
      :role-id="selectedRole.id"
      class="shrink-0"
    />
  </div>

  <div class="mt-3 flex flex-wrap items-center gap-2">
    <LocationDisplay :data="selectedRole" text-size="sm" />
    <ScrapedBadge v-if="selectedRole?.is_scraped" />
  </div>

  <div v-if="minimal" class="mt-2 cursor-default flex items-center justify-end w-full gap-2 text-sm">
    <router-link
      :to="`/jobs/${selectedRole?.id}`"
      class="bg-white border-[1px] border-gray-200 rounded-md px-2 py-1  text-gray-500 hover:bg-cosmicPurple/10 transition-colors transition-duration-200 truncate flex items-center"
    >
    <FileText class="w-3.5 h-3.5 mr-2" />
      View on TrackTalent
    </router-link>

    <a
      :href="selectedRole?.url"
      target="_blank"
      class="bg-white border-[1px] border-gray-200 rounded-md px-2 py-1 text-gray-500 hover:bg-cosmicPurple/10 transition-colors transition-duration-200 truncate flex items-center"
    >
    <SquareArrowOutUpRight class="w-3.5 h-3.5 mr-2" />
      View on company website
    </a>
  </div>

  <!-- Fields -->
  <dl
    v-if="!minimal"
    class="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm"
  >
    <div :class="rowClass">
      <dt :class="labelClass"><Link class="h-3.5 w-3.5" />Posting</dt>
      <dd class="min-w-0">
        <a
          :href="selectedRole?.url"
          target="_blank"
          rel="noopener"
          class="inline-flex max-w-full items-center gap-1 text-cosmicPurple hover:underline"
        >
          <span class="truncate">{{ postingHost }}</span>
          <SquareArrowOutUpRight class="h-3 w-3 shrink-0" />
        </a>
      </dd>
    </div>

    <div :class="rowClass">
      <dt :class="labelClass"><ChartColumnBig class="h-3.5 w-3.5" />Seniority</dt>
      <dd class="text-gray-900">
        <SeniorityLabel
          v-if="selectedRole?.seniority_level"
          class="!text-sm"
          :seniority-level="selectedRole.seniority_level"
        />
      </dd>
    </div>

    <div v-if="selectedRole?.work_authorization_requirement" :class="rowClass">
      <dt :class="labelClass"><EarthLock class="h-3.5 w-3.5" />Work authorization</dt>
      <dd class="text-gray-900">
        {{
          authorizationRequirements.find(
            (requirement) => requirement.id === selectedRole?.work_authorization_requirement
          )?.label
        }}
      </dd>
    </div>

    <div v-if="selectedRole?.microsoft_role_id || selectedRole?.job_id" :class="rowClass">
      <dt :class="labelClass"><FileDigit class="h-3.5 w-3.5" />Job ID</dt>
      <dd class="truncate text-gray-900">{{ selectedRole?.microsoft_role_id || selectedRole?.job_id }}</dd>
    </div>

    <div v-if="selectedRole?.created_at" :class="rowClass">
      <dt :class="labelClass"><Calendar class="h-3.5 w-3.5" />Posted</dt>
      <dd class="text-gray-900">{{ formatDate(selectedRole.created_at) }}</dd>
    </div>

    <slot name="fields" :row-class="rowClass" :label-class="labelClass" />
  </dl>
</template>

<script setup lang="ts">
import { computed, withDefaults } from 'vue'
import { storeToRefs } from 'pinia'
import { Building, Link, ChartColumnBig, EarthLock, FileDigit, FileText, SquareArrowOutUpRight, Calendar } from 'lucide-vue-next'
import { useRolesStore } from '@/stores/roles'
import LocationDisplay from '@/components/common/LocationDisplay.vue'
import JobStatusDropdown from '@/components/jobs/JobStatusDropdown.vue'
import ScrapedBadge from '@/components/common/badges/ScrapedBadge.vue'
import SeniorityLabel from '@/components/common/SeniorityLabel.vue'
import authorizationRequirements from '@/const/authorizationRequirements'
import type { OpenRole } from '@/types/openRole'
import { formatDate } from '@/utils/formatDate'

interface Props {
  minimal?: boolean
  roleId?: string
  roleData?: any // For direct role data passed from applications
  showCandidateInfo?: boolean // Whether to show candidate name (for guides)
  candidateData?: any // Candidate information
  dateLabel?: string // "Added" or "Updated"
  showDate?: boolean // Whether to show the date section
  additionalBadges?: boolean // Whether to show additional badges like referral request
}

const props = withDefaults(defineProps<Props>(), {
  showCandidateInfo: false,
  dateLabel: 'Added',
  showDate: true,
  additionalBadges: false
})

const roleStore = useRolesStore()
const { selectedRole: selectedRoleRef } = storeToRefs(roleStore)

// Computed property that uses either store data or direct props data
const selectedRole = computed<OpenRole | null>(() => {
  if (props.roleData) {
    return props.roleData
  }
  return selectedRoleRef.value
})

const rowClass = 'grid grid-cols-[9rem_minmax(0,1fr)] items-center gap-3 px-3 py-2'
const labelClass = 'flex items-center gap-2 text-xs font-medium text-gray-500'

const postingHost = computed(() => {
  const url = selectedRole.value?.url
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
})

const roleStatus = computed({
  get: () => !!selectedRole.value?.status_open,
  set: () => {} // Handled by JobStatusDropdown
})
</script>
